import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTrackOpenItem } from '../hooks/useTrackOpenItem';
import { useActiveOrg } from '../lib/activeOrg';
import { useProjectCanvas } from '../lib/projects/useProjectCanvas';
import { useSaveProject, type ProjectPatch } from '../lib/projects/useSaveProject';
import { useReorderBuildOrder } from '../lib/projects/useReorderBuildOrder';
import { computeProgress } from '../components/projects/helpers';
import { ProjectHeader } from '../components/projects/ProjectHeader';
import { ProjectBuildOrderSection } from '../components/projects/ProjectBuildOrderSection';
import { EpicsSection } from '../components/projects/EpicsSection';
import { ProjectWorkItemsSection } from '../components/projects/ProjectWorkItemsSection';
import { ProjectDocumentsSection } from '../components/projects/ProjectDocumentsSection';
import { ProjectArtifactsSection } from '../components/projects/ProjectArtifactsSection';
import { ProjectSettingsSheet } from '../components/projects/ProjectSettingsSheet';
import { AttachExistingModal } from '../components/projects/attach-existing/AttachExistingModal';
import { SectionNewMenu } from '../components/projects/SectionNewMenu';
import { EditorCover, type EditorCoverTarget } from '../components/EditorCover';
import type { AddKind } from '../lib/projects/useAddableItems';
import { useCreateDocument } from '../lib/projects/useCreateDocument';
import {
  useCreateWorkItem,
  type CreatableWorkItemType
} from '../lib/projects/useCreateWorkItem';
import { useRealtimeRowReload } from '../hooks/useRealtimeRowReload';
import { ButtonRouterLink, EmptyState, Spinner, Toast } from '../components/primitives';
import type { MenuItem } from '../components/primitives/Menu';
import './ProjectCanvas.css';

export function ProjectCanvas() {
  const { id } = useParams<{ id: string }>();
  const { activeOrgId } = useActiveOrg();
  const { state, data, error, reload } = useProjectCanvas(activeOrgId, id);
  // The full-screen editor cover: opening an existing doc/work-item, or a
  // freshly-created one. Replaces the old metadata side-sheets.
  const [cover, setCover] = useState<EditorCoverTarget | null>(null);
  // Bumped on cover close (and on attach) so the self-fetching Work Items board
  // and Documents gallery/table refetch in place after an item/doc is created,
  // attached or edited — otherwise a new row only shows after a page refresh.
  const [boardReload, setBoardReload] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addKind, setAddKind] = useState<AddKind | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const docCreate = useCreateDocument();
  const wiCreate = useCreateWorkItem();
  const { save } = useSaveProject();
  const { reorder } = useReorderBuildOrder();

  // One autosave handler shared by the inline header fields (title/description)
  // and the settings panel (status/lead/target/DoD). Each writes only the keys
  // it touches; a successful write reloads the canvas so the header re-renders.
  const persistProject = useCallback(
    async (patch: Partial<ProjectPatch>): Promise<boolean> => {
      if (!activeOrgId || !id) return false;
      const ok = await save(activeOrgId, id, patch);
      if (ok) reload();
      return ok;
    },
    [activeOrgId, id, save, reload]
  );

  // Live canvas: subscribe to ALL work-item changes in this project and refresh.
  // No-ops while `id` is undefined; `reload` is a stable setTick updater.
  useRealtimeRowReload({ table: 'work_items', rowId: id, filterColumn: 'project_id', onChange: reload });

  const projectName = state === 'ready' ? data?.project.name : undefined;
  useTrackOpenItem('project', id, projectName);

  if (state === 'loading' || state === 'no-org') {
    return (
      <div className="pn-canvas-status" data-testid="project-canvas-loading">
        <Spinner label="Loading project" />
      </div>
    );
  }
  if (state === 'not-found') {
    return (
      <div className="pn-canvas-status">
        <EmptyState
          data-testid="project-canvas-not-found"
          action={<ButtonRouterLink to="/projects">Back to projects</ButtonRouterLink>}
        >
          This project doesn’t exist in your org.
        </EmptyState>
      </div>
    );
  }
  if (state === 'error' || !data) {
    return (
      <div className="pn-canvas-status">
        <EmptyState data-testid="project-canvas-error">
          Couldn’t load this project{error ? `: ${error}` : ''}.
        </EmptyState>
      </div>
    );
  }

  const { project, workItems, leadName, dependencies } = data;
  const { done, total } = computeProgress(workItems);

  // Closing the cover refreshes the canvas so newly created / edited items show.
  const closeCover = () => {
    setCover(null);
    reload();
    setBoardReload((n) => n + 1);
  };

  const createWorkItem = async (type: CreatableWorkItemType) => {
    if (!activeOrgId) return;
    const newId = await wiCreate.create(activeOrgId, project.id, type);
    if (newId) setCover({ kind: 'workItem', id: newId });
    else setCreateError(wiCreate.error ?? 'Couldn’t create the work item.');
  };
  const createDocument = async () => {
    if (!activeOrgId) return;
    const newId = await docCreate.create(activeOrgId, project.id);
    if (newId) setCover({ kind: 'document', id: newId });
    else setCreateError(docCreate.error ?? 'Couldn’t create the document.');
  };

  const epicsItems: MenuItem[] = [
    { id: 'create', label: 'Create epic', 'data-testid': 'epics-create', onSelect: () => void createWorkItem('epic') },
    { id: 'attach', label: 'Attach existing…', 'data-testid': 'epics-attach', onSelect: () => setAddKind('epics') }
  ];
  const workItemsItems: MenuItem[] = [
    { id: 'epic', label: 'Create epic', 'data-testid': 'work-items-create-epic', onSelect: () => void createWorkItem('epic') },
    { id: 'story', label: 'Create story', 'data-testid': 'work-items-create-story', onSelect: () => void createWorkItem('story') },
    { id: 'bug', label: 'Create bug', 'data-testid': 'work-items-create-bug', onSelect: () => void createWorkItem('bug') },
    { id: 'task', label: 'Create task', 'data-testid': 'work-items-create-task', onSelect: () => void createWorkItem('task') },
    { id: 'attach', label: 'Attach existing…', 'data-testid': 'work-items-attach', onSelect: () => setAddKind('work-items') }
  ];
  const documentsItems: MenuItem[] = [
    { id: 'create', label: 'Create document', 'data-testid': 'documents-create', onSelect: () => void createDocument() },
    { id: 'attach', label: 'Attach existing…', 'data-testid': 'documents-attach', onSelect: () => setAddKind('documents') }
  ];

  return (
    <div
      className="pn-canvas-split"
      data-testid="project-canvas-split"
    >
      <div className="pn-canvas" data-testid="project-canvas">
        <div className="pn-canvas-inner">
        <ProjectHeader project={project} leadName={leadName} doneCount={done} totalCount={total} onEditProject={() => setSettingsOpen(true)} onPatch={persistProject} />
        <ProjectBuildOrderSection
          items={workItems}
          dependencies={dependencies}
          onReorder={
            activeOrgId
              ? (parentId, sequence) =>
                  // Scoped to the dragged SIBLING GROUP: currentItems is the
                  // group under `parentId` (null = top level) and `sequence` is
                  // its full permutation (05-project-hub-build-order).
                  reorder(
                    activeOrgId,
                    workItems
                      .filter((w) => (w.parent_work_item_id ?? null) === parentId)
                      .map((w) => ({ id: w.id, build_order: w.build_order })),
                    sequence
                  )
              : undefined
          }
        />
        <EpicsSection
          items={workItems}
          onOpenWorkItem={(itemId) => setCover({ kind: 'workItem', id: itemId })}
          newMenu={<SectionNewMenu label="New epic" menuLabel="Add epic to project" triggerTestId="epics-add" items={epicsItems} />}
        />
        <ProjectWorkItemsSection
          items={workItems}
          activeOrgId={activeOrgId}
          projectId={project.id}
          reloadSignal={boardReload}
          onOpenWorkItem={(itemId) => setCover({ kind: 'workItem', id: itemId })}
          newMenu={<SectionNewMenu label="New item" menuLabel="Add work item to project" triggerTestId="work-items-add" items={workItemsItems} />}
        />
        <ProjectDocumentsSection
          projectId={project.id}
          reloadSignal={boardReload}
          onOpenDocument={(docId) => setCover({ kind: 'document', id: docId })}
          newMenu={<SectionNewMenu label="New doc" menuLabel="Add document to project" triggerTestId="documents-add" items={documentsItems} />}
        />
        <ProjectArtifactsSection />
        <ProjectSettingsSheet
          open={settingsOpen}
          project={project}
          onClose={() => setSettingsOpen(false)}
          onPatch={persistProject}
        />
        {addKind ? (
          <AttachExistingModal
            open
            kind={addKind}
            projectId={project.id}
            onClose={() => setAddKind(null)}
            onDone={() => {
              // Refresh canvas data (epics list) AND the self-fetching Work
              // Items / Documents sections, which key off `boardReload`.
              reload();
              setBoardReload((n) => n + 1);
            }}
          />
        ) : null}
        </div>
        <EditorCover target={cover} onNavigate={setCover} onClose={closeCover} />
        {createError ? (
          <Toast onDismiss={() => setCreateError(null)} data-testid="project-canvas-create-error">
            {createError}
          </Toast>
        ) : null}
      </div>
    </div>
  );
}

